import type { ComponentType, ReactElement } from 'react';
import { Fragment } from 'react';
import type { ResolvedSceneNode, SceneNodeType } from '../../../../../src/build-engines/studio/types.js';
import { warnUnsupportedNode, type StudioNodeContext, type StudioNodeRendererProps } from './box';
import { EyebrowNode, HeadlineNode, SubheadlineNode, BodyNode, CaptionNode, LabelNode, QuoteNode } from './typography';
import {
  MetricNode,
  MetricGroupNode,
  PercentageNode,
  BudgetCalloutNode,
  ReachCalloutNode,
  StatRailNode,
} from './metrics';
import { ImageNode, ImageMaskNode, HeroImageNode, DeviceMockupNode, LogoNode, IconNode } from './media';
import {
  AudienceCardNode,
  TacticCardNode,
  EvidenceCardNode,
  QuoteCardNode,
  ComparisonCardNode,
  ProductModuleNode,
} from './modules';
import {
  MapNode,
  ChartNode,
  DonutNode,
  BarNode,
  TimelineNode,
  JourneyStageNode,
  FunnelNode,
  ConnectorNode,
  FlowNode,
} from './data';
import { GradientFieldNode, ShapeNode, RuleNode, LineworkNode, BackgroundTextureNode } from './decoration';
import {
  SlideNode,
  StackNode,
  RowNode,
  SplitNode,
  GridNode,
  ClusterNode,
  OverlayNode,
  BleedNode,
  InsetNode,
  RailNode,
  HeroNode,
  CanvasRegionNode,
  FooterNode,
  PageNumberNode,
  AgencyClientLockupNode,
} from './structure';

/** One React component per node type in the closed `SceneNodeType` catalog (outcome #1). */
const NODE_RENDERERS: Record<SceneNodeType, ComponentType<StudioNodeRendererProps>> = {
  // Structural
  Slide: SlideNode,
  Stack: StackNode,
  Row: RowNode,
  Split: SplitNode,
  Grid: GridNode,
  Cluster: ClusterNode,
  Overlay: OverlayNode,
  Bleed: BleedNode,
  Inset: InsetNode,
  Rail: RailNode,
  Hero: HeroNode,
  CanvasRegion: CanvasRegionNode,
  // Typography
  Eyebrow: EyebrowNode,
  Headline: HeadlineNode,
  Subheadline: SubheadlineNode,
  Body: BodyNode,
  Caption: CaptionNode,
  Label: LabelNode,
  Quote: QuoteNode,
  // Metrics
  Metric: MetricNode,
  MetricGroup: MetricGroupNode,
  Percentage: PercentageNode,
  BudgetCallout: BudgetCalloutNode,
  ReachCallout: ReachCalloutNode,
  StatRail: StatRailNode,
  // Media
  Image: ImageNode,
  ImageMask: ImageMaskNode,
  HeroImage: HeroImageNode,
  DeviceMockup: DeviceMockupNode,
  Logo: LogoNode,
  Icon: IconNode,
  // Modules
  AudienceCard: AudienceCardNode,
  TacticCard: TacticCardNode,
  EvidenceCard: EvidenceCardNode,
  QuoteCard: QuoteCardNode,
  ComparisonCard: ComparisonCardNode,
  ProductModule: ProductModuleNode,
  // Data
  Map: MapNode,
  Chart: ChartNode,
  Donut: DonutNode,
  Bar: BarNode,
  Timeline: TimelineNode,
  JourneyStage: JourneyStageNode,
  Funnel: FunnelNode,
  Connector: ConnectorNode,
  Flow: FlowNode,
  // Decoration
  GradientField: GradientFieldNode,
  Shape: ShapeNode,
  Rule: RuleNode,
  Linework: LineworkNode,
  BackgroundTexture: BackgroundTextureNode,
  // Global chrome (no-op; rendered by Frame)
  Footer: FooterNode,
  PageNumber: PageNumberNode,
  AgencyClientLockup: AgencyClientLockupNode,
};

/**
 * Renders one resolved node plus every descendant, FLAT — every
 * `ResolvedSceneNode.box` is already absolute canvas px in the SAME
 * 1920x1080 coordinate space regardless of nesting depth (see the type's own
 * doc comment), so a node's children are rendered as siblings positioned by
 * their own box, never nested inside a positioned parent (which would shift
 * their containing block and break that global coordinate assumption).
 */
export function StudioNode({ node, ctx }: { node: ResolvedSceneNode; ctx: StudioNodeContext }): ReactElement {
  const Renderer = NODE_RENDERERS[node.type];
  if (!Renderer) {
    warnUnsupportedNode(node);
  }
  return (
    <Fragment key={node.id}>
      {Renderer ? <Renderer node={node} ctx={ctx} /> : null}
      {node.children?.map((child) => (
        <StudioNode key={child.id} node={child} ctx={ctx} />
      ))}
    </Fragment>
  );
}
